import { useEffect } from "react";
import { Form, Input, DatePicker, Skeleton, Select, TextArea } from "../../components/ui";
import {
  useAddHomework,
  useGetHomeworkById,
  useUpdateHomework,
} from "../../query/homeworkQuery";
import { useGetAllTeachers } from "../../query/teacherQuery";
import { useGetAllClasses } from "../../query/classQuery";
import { useGetAllChildren } from "../../query/childQuery";
import swal from "../../utils/swal";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { ASSIGNEE_TYPES, HOMEWORK_TYPES } from "./homeworkHelpers";
import "../teachers/addTeacher.css";
import "./homework.css";

function asDateString(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.format) return value.format("YYYY-MM-DD");
  return "";
}

function teacherLabel(teacher) {
  return `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "Teacher";
}

function childLabel(child) {
  return `${child.firstName || ""} ${child.lastName || ""}`.trim() || "Student";
}

function AddHomework() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const assignee = Form.useWatch("assignee", form) || "CHILD";

  const { mutate: addHomework, isPending: isAdding } = useAddHomework();
  const { mutate: updateHomework, isPending: isUpdating } = useUpdateHomework();
  const { data: homework, isLoading } = useGetHomeworkById(isEdit ? id : undefined);
  const { data: teachersData, isLoading: loadingTeachers } = useGetAllTeachers({ page: 1, limit: 200 });
  const { data: classesData, isLoading: loadingClasses } = useGetAllClasses({ page: 1, limit: 200 });
  const { data: childrenData, isLoading: loadingChildren } = useGetAllChildren({ page: 1, limit: 200 });

  const teachers = teachersData?.docs || [];
  const classrooms = classesData?.docs || [];
  const children = childrenData?.docs || [];
  const isMutating = isAdding || isUpdating;

  useEffect(() => {
    if (!isEdit || !homework) return;
    form.setFieldsValue({
      title: homework.title || "",
      description: homework.description || "",
      type: homework.type || "HOMEWORK",
      assignee: homework.assignee || "CHILD",
      teacher: homework.teacher?._id || homework.teacher || "",
      classroom: homework.classroom?._id || homework.classroom || "",
      children: homework.children?._id || homework.children || "",
      dueDate: homework.dueDate ? dayjs(homework.dueDate) : null,
    });
  }, [isEdit, homework, form]);

  const onFinish = (values) => {
    const payload = {
      title: values.title,
      description: values.description,
      type: values.type,
      assignee: values.assignee,
      teacher: values.teacher,
      dueDate: asDateString(values.dueDate),
      classroom: values.assignee === "CLASS" ? values.classroom : null,
      children: values.assignee === "CHILD" ? values.children : null,
    };

    if (isEdit) {
      updateHomework(
        { id, ...payload },
        {
          onSuccess: () => {
            swal("Success", "Homework updated successfully", "success");
            navigate(`/homework/${id}`);
          },
          onError: (error) => swal("Error", error?.message || "Failed to update homework", "error"),
        }
      );
      return;
    }

    addHomework(payload, {
      onSuccess: () => {
        swal("Success", "Homework created successfully", "success");
        navigate("/homework");
      },
      onError: (error) => swal("Error", error?.message || "Failed to create homework", "error"),
    });
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off" initialValues={{ type: "HOMEWORK", assignee: "CHILD" }}>
        <section className="tf-card">
          <h2 className="tf-card-head">{isEdit ? "Edit Homework" : "New Homework"}</h2>
          <div className="tf-card-body">
            <div className="tf-field">
              <p className="tf-label">
                Title <span>*</span>
              </p>
              <Form.Item
                name="title"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter title" }]}
              >
                <Input placeholder="Reading assignment" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Description <span>*</span>
              </p>
              <Form.Item
                name="description"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter description" }]}
              >
                <TextArea rows={5} placeholder="Describe the homework..." />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Type <span>*</span>
              </p>
              <Form.Item
                name="type"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select type" }]}
              >
                <Select placeholder="Select type" options={HOMEWORK_TYPES} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Assignee <span>*</span>
              </p>
              <Form.Item
                name="assignee"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select assignee" }]}
              >
                <Select placeholder="Select assignee" options={ASSIGNEE_TYPES} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Teacher <span>*</span>
              </p>
              <Form.Item
                name="teacher"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select teacher" }]}
              >
                <Select
                  placeholder="Select teacher"
                  loading={loadingTeachers}
                  options={teachers.map((teacher) => ({
                    value: teacher._id,
                    label: teacherLabel(teacher),
                  }))}
                />
              </Form.Item>
            </div>

            {assignee === "CLASS" ? (
              <div className="tf-field">
                <p className="tf-label">
                  Classroom <span>*</span>
                </p>
                <Form.Item
                  name="classroom"
                  className="tf-item mb-0"
                  rules={[{ required: true, message: "Please select classroom" }]}
                >
                  <Select
                    placeholder="Select classroom"
                    loading={loadingClasses}
                    options={classrooms.map((room) => ({
                      value: room._id,
                      label: room.classroomName || room.name || room.className || "Classroom",
                    }))}
                  />
                </Form.Item>
              </div>
            ) : (
              <div className="tf-field">
                <p className="tf-label">
                  Student <span>*</span>
                </p>
                <Form.Item
                  name="children"
                  className="tf-item mb-0"
                  rules={[{ required: true, message: "Please select student" }]}
                >
                  <Select
                    placeholder="Select student"
                    loading={loadingChildren}
                    options={children.map((child) => ({
                      value: child._id,
                      label: childLabel(child),
                    }))}
                  />
                </Form.Item>
              </div>
            )}

            <div className="tf-field">
              <p className="tf-label">
                Due Date <span>*</span>
              </p>
              <Form.Item
                name="dueDate"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select due date" }]}
              >
                <DatePicker placeholder="Select due date" />
              </Form.Item>
            </div>
          </div>
        </section>

        <div className="tf-actions">
          <button type="button" className="tf-btn is-ghost" onClick={() => navigate("/homework")}>
            Cancel
          </button>
          <button type="submit" className="tf-btn is-primary" disabled={isMutating}>
            {isMutating ? "Saving..." : isEdit ? "Save" : "Create Homework"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddHomework;
